import { ActivityIndicator, View } from "react-native";
import { router } from "expo-router";
import { useEffect, useState, useMemo } from "react";
import { KanbanDrawer } from "@/components/Kanban/Drawer";
import { KanbanView } from "@/components/Kanban";
import { shiftsService } from "@/services/shiftService";
import { ShiftType } from "@/types/db/shifts";
import useAuth from "@/hooks/useAuth";
import { getShiftKanbanConfig, MarketOption } from "@/config/tableConfigs/shiftKanbanConfig";
import { Query } from "@/components/Table/types";
import { useCalendarParams } from "@/lib/calendar";
import { useTheme } from "@/hooks/useTheme";
import { supabase } from "@/lib/supabase-client";
import { useSetPageTitle } from "@/hooks/useSetPageTitle";

export default function Schedules() {
  const { user, displayNames } = useAuth();
  const { colors } = useTheme();
  const { date, daysView } = useCalendarParams();
  const [shifts, setShifts] = useState<ShiftType[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState<Query<string>>();
  const [shiftStats, setShiftStats] = useState<{
    pastShifts: number;
    pastIssues: number;
    futureShifts: number;
    futureIssues: number;
  }>({ pastShifts: 0, pastIssues: 0, futureShifts: 0, futureIssues: 0 });
  const [marketOptions, setMarketOptions] = useState<MarketOption[]>([]);

  useSetPageTitle(displayNames?.shifts?.plural);

  // Fetch markets on mount
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .schema("lists")
        .from("markets")
        .select("id, name")
        .order("name", { ascending: true });

      if (data) {
        setMarketOptions(data.map((m) => ({ value: m.id, label: m.name })));
      }
    })();
  }, []);

  const config = useMemo(() => getShiftKanbanConfig(marketOptions, displayNames), [marketOptions, displayNames]);

  // Calculate date range from calendar params
  const dateRange = useMemo(() => {
    const startDate = new Date(date);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(date);
    endDate.setDate(endDate.getDate() + daysView + 1); // +1 to include full last day
    endDate.setHours(0, 0, 0, 0);

    return { startDate, endDate };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date.getTime(), daysView]);

  // Filter shifts based on query filters
  const filteredShifts = useMemo(() => {
    if (!query?.filters?.length) return shifts;

    return shifts.filter((shift) => {
      // Check each filter
      return query.filters.every((filter) => {
        if (!filter.expressions?.length) return true;

        const getValue = (fieldName: string, subfield?: string): string | number => {
          switch (fieldName) {
            case "project":
              if (subfield === "id") return shift.project?.id || "";
              return shift.project?.name?.toLowerCase() || "";
            case "client":
              if (subfield === "id") return shift.project?.client?.id || "";
              return shift.project?.client?.name?.toLowerCase() || "";
            case "location":
              if (subfield === "id") return shift.location?.id || shift.project?.location?.id || "";
              return (shift.location?.name || shift.project?.location?.name)?.toLowerCase() || "";
            case "start_time":
              return shift.start_time ? new Date(shift.start_time).toISOString() : "";
            case "duration":
              // Compute duration in hours
              if (shift.start_time && shift.end_time) {
                const start = new Date(shift.start_time).getTime();
                const end = new Date(shift.end_time).getTime();
                return Math.round(((end - start) / (1000 * 60 * 60)) * 10) / 10;
              }
              return 0;
            case "status":
              return shift.status?.toUpperCase() || "";
            default:
              return "";
          }
        };

        // Special handling for markets (array of objects)
        if (filter.name === "markets") {
          const shiftMarketIds = (shift as any).markets?.map((m: any) => m.id) || [];
          return filter.expressions.some((expr) => {
            const exprValue = expr.value as string | string[];
            if (expr.operator === "in" && Array.isArray(exprValue)) {
              return shiftMarketIds.some((id: string) => exprValue.includes(id));
            }
            if (expr.operator === "nin" && Array.isArray(exprValue)) {
              return !shiftMarketIds.some((id: string) => exprValue.includes(id));
            }
            if (expr.operator === "is_empty") {
              return shiftMarketIds.length === 0;
            }
            if (expr.operator === "is_not_empty") {
              return shiftMarketIds.length > 0;
            }
            return true;
          });
        }

        // Check if any expression matches (OR within field)
        return filter.expressions.some((expr) => {
          const fieldValue = getValue(filter.name, expr.subfield);
          const searchValue = expr.value;

          // Handle numeric comparisons for duration
          if (typeof fieldValue === "number") {
            const numValue = Number(searchValue);
            switch (expr.operator) {
              case "eq":
                return fieldValue === numValue;
              case "neq":
                return fieldValue !== numValue;
              case "gt":
                return fieldValue > numValue;
              case "gte":
                return fieldValue >= numValue;
              case "lt":
                return fieldValue < numValue;
              case "lte":
                return fieldValue <= numValue;
              default:
                return true;
            }
          }

          // Handle string comparisons
          const strFieldValue = String(fieldValue).toLowerCase();
          switch (expr.operator) {
            case "eq":
              return strFieldValue === String(searchValue).toLowerCase();
            case "neq":
              return strFieldValue !== String(searchValue).toLowerCase();
            case "like":
              return strFieldValue.includes(String(searchValue).toLowerCase());
            case "in":
              // "any of" - value is array of selected values
              if (Array.isArray(searchValue)) {
                return searchValue.some((v) => String(v).toLowerCase() === strFieldValue);
              }
              return strFieldValue === String(searchValue).toLowerCase();
            case "nin":
              // "is none of" - value is array of excluded values
              if (Array.isArray(searchValue)) {
                return !searchValue.some((v) => String(v).toLowerCase() === strFieldValue);
              }
              return strFieldValue !== String(searchValue).toLowerCase();
            default:
              return true;
          }
        });
      });
    });
  }, [shifts, query?.filters]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    let intervalId: NodeJS.Timeout;

    async function fetchShifts() {
      try {
        const [data, stats] = await Promise.all([
          shiftsService.getShifts({
            startDate: dateRange.startDate.toISOString(),
            endDate: dateRange.endDate.toISOString(),
          }),
          shiftsService.getShiftStats(dateRange.startDate),
        ]);
        setShifts(data);
        setShiftStats(stats);
      } catch (error) {
        console.error("Error fetching shifts:", error);
      }
    }

    fetchShifts().finally(() => setLoading(false));
    intervalId = setInterval(fetchShifts, 30_000);

    return () => clearInterval(intervalId);
  }, [user, dateRange]);

  const handleShiftClick = (shiftId: string) => {
    router.setParams({ id: shiftId, panel: "shift" });
  };

  return (
    <View style={{ flexDirection: "row", height: "100%" }}>
      <KanbanDrawer
        showCalendarControls={true}
        filterChoices={config.filterChoices}
        page="schedules"
        onQueryChange={setQuery}
      />
      <View style={{ padding: 16, width: "auto", flex: 1 }}>
        <KanbanView
          events={filteredShifts}
          allEvents={shifts}
          onShiftPress={handleShiftClick}
          shiftDisplayMode={"start"}
          visibleFields={query?.visible}
          shiftStats={shiftStats}
        />
      </View>
      {loading && (
        <ActivityIndicator
          size={80}
          style={{
            backgroundColor: colors.backdrop,
            borderTopWidth: 1,
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            flex: 1,
          }}
        />
      )}
    </View>
  );
}
